import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChartBar,
  Users,
  Buildings,
  Lifebuoy,
  Lightbulb,
  Package,
  CreditCard,
  SignOut,
  List,
  X,
  Gear,
  UserCircle,
  Wrench,
  Activity,
  Megaphone,
} from "phosphor-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// REMOVED: Deleted Tenants from sidebar - now handled via filters in Tenants page
const navItems = [
  { path: "/admin", label: "Dashboard", icon: ChartBar, exact: true },
  { path: "/admin/tenants", label: "Tenants", icon: Buildings },
  { path: "/admin/tenant-communications", label: "Tenant Comms", icon: Megaphone },
  { path: "/admin/tickets", label: "Support Tickets", icon: Lifebuoy },
  { path: "/admin/ideas", label: "Enhancement Ideas", icon: Lightbulb },
  { path: "/admin/features", label: "Features", icon: Package },
  { path: "/admin/bundles", label: "Bundles", icon: Package },
  { path: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard },
  { path: "/admin/admins", label: "Administrators", icon: Users },
  { path: "/admin/system", label: "System Utilities", icon: Wrench },
  { path: "/admin/system/health", label: "System Health", icon: Activity },
];

export default function AdminLayout({ children, user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // GUARANTEED SCROLL TO TOP for Admin pages on route change
  // Fixes iOS Safari issue where navigation causes partial scroll down
  useEffect(() => {
    const resetScrollAndCleanup = () => {
      const bodyStyle = document.body.style;
      const isLocked = bodyStyle.position === 'fixed' || bodyStyle.overflow === 'hidden';
      
      // DEBUG: Log state on route change
      if (process.env.NODE_ENV === 'development') {
        console.log('[AdminLayout] Route change - resetting scroll:', {
          path: location.pathname,
          isLocked,
          hasDialogScrollLock: !!document.body.dataset.dialogScrollLock
        });
      }
      
      // If scroll is locked but no modal should be open, force restore
      if (isLocked && !document.body.dataset.dialogScrollLock) {
        // Clear all scroll lock styles
        document.documentElement.style.overflow = '';
        bodyStyle.overflow = '';
        bodyStyle.position = '';
        bodyStyle.top = '';
        bodyStyle.left = '';
        bodyStyle.right = '';
        bodyStyle.width = '';
      } else if (!isLocked) {
        // Even if not locked, ensure styles are clean
        document.documentElement.style.overflow = '';
        bodyStyle.overflow = '';
        bodyStyle.position = '';
        bodyStyle.top = '';
        bodyStyle.left = '';
        bodyStyle.right = '';
        bodyStyle.width = '';
      }
      
      // ALWAYS scroll to absolute top on route change (fixes iOS auto-scroll bug)
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // Run immediately on mount/route change
    resetScrollAndCleanup();
    
    // Also run after a short delay to catch any layout shifts
    const timeoutId = setTimeout(resetScrollAndCleanup, 50);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Simple body scroll lock for mobile sidebar only
  // Uses a different approach - only overflow, not fixed positioning
  useEffect(() => {
    if (sidebarOpen) {
      // Only set overflow, don't use fixed positioning for sidebar
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    // Clear localStorage token as well
    localStorage.removeItem('auth_token');
    window.location.href = "/";
  };

  // Robust close function that works on iOS
  const closeMenu = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSidebarOpen(false);
  };

  const NavLink = ({ item }) => {
    const isActive = item.exact 
      ? location.pathname === item.path 
      : location.pathname.startsWith(item.path);
    const Icon = item.icon;

    const handleNavClick = (e) => {
      // Close menu immediately on touch/click
      setSidebarOpen(false);
    };

    return (
      <Link
        to={item.path}
        onClick={handleNavClick}
        onTouchEnd={handleNavClick}
        className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all ${
          isActive ? "bg-[#FF6600] text-white" : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
        }`}
        style={{ 
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation'
        }}
      >
        <Icon size={20} weight={isActive ? "fill" : "regular"} />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-zinc-900 border-b border-zinc-800 z-40 flex items-center justify-between px-4">
        <button 
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="p-2 text-white active:bg-zinc-800 rounded-sm"
          data-testid="admin-mobile-menu-btn"
        >
          <List size={24} weight="bold" />
        </button>
        <span className="text-white font-heading font-bold">ADMIN PORTAL</span>
        <button onClick={handleLogout} className="p-2 text-zinc-400">
          <SignOut size={24} />
        </button>
      </header>

      {/* Mobile Overlay - Button for iOS compatibility */}
      {sidebarOpen && (
        <button
          type="button"
          className="lg:hidden fixed inset-0 bg-black/50 z-50 cursor-pointer border-none outline-none"
          onClick={closeMenu}
          onTouchEnd={closeMenu}
          aria-label="Close menu"
          data-testid="admin-sidebar-overlay"
          style={{ 
            WebkitTapHighlightColor: 'transparent',
            WebkitTouchCallout: 'none',
            touchAction: 'manipulation'
          }}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-zinc-900 border-r border-zinc-800 z-[60] transform transition-transform duration-200 ease-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF6600] rounded-sm flex items-center justify-center">
              <Gear size={18} className="text-white" />
            </div>
            <span className="text-white font-heading font-bold text-sm">ADMIN PORTAL</span>
          </div>
          {/* Close Button - iOS-compatible */}
          <button 
            type="button"
            onClick={closeMenu}
            onTouchEnd={closeMenu}
            className="lg:hidden p-3 text-zinc-400 active:bg-zinc-800 rounded-sm cursor-pointer"
            style={{ 
              WebkitTapHighlightColor: 'transparent',
              WebkitTouchCallout: 'none', 
              WebkitUserSelect: 'none',
              touchAction: 'manipulation'
            }}
            data-testid="admin-close-sidebar-btn"
          >
            <X size={24} weight="bold" />
          </button>
        </div>

        <nav className="flex flex-col h-[calc(100%-8rem)] justify-between py-4 overflow-y-auto">
          <div className="space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </div>
          <div className="px-4 space-y-2">
            <div className="p-3 bg-zinc-800 rounded-sm">
              <div className="flex items-center gap-2">
                <UserCircle size={32} className="text-zinc-500" />
                <div>
                  <p className="text-sm text-white font-medium">{user?.name}</p>
                  <p className="text-xs text-zinc-500">{user?.email}</p>
                </div>
              </div>
            </div>
            <Button onClick={handleLogout} variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800">
              <SignOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 bg-zinc-900 border-b border-zinc-800 items-center justify-between px-8">
          <h1 className="text-white font-heading text-lg font-semibold uppercase tracking-tight">
            {navItems.find((i) => i.exact ? location.pathname === i.path : location.pathname.startsWith(i.path))?.label || "Admin"}
          </h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <UserCircle size={24} className="text-zinc-500" />
              <span className="text-sm text-white">{user?.name}</span>
            </div>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
              <SignOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </header>

        <div className="p-4 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
